import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ship, Anchor, Clock, MapPin } from "lucide-react";
import Layout from "@/components/Layout";
import client from "@/lib/api";

function fmtSail(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function InterIslandPorts() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    client.get("/shipments")
      .then((r) => setJobs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const maritime = jobs.filter((j) => j.vessel_name || j.departure_sailing_time || j.dock_terminal_slot);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-[10px] font-mono font-bold text-[#059669] tracking-widest uppercase">Caribbean Maritime</span>
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-[#0B132B] tracking-tight flex items-center gap-2">
          <Ship className="h-7 w-7" strokeWidth={2} /> Inter-Island Ports
        </h1>
        <p className="text-sm text-ink-2 mt-1 mb-6 max-w-2xl">Vessel sailings and dock/terminal handovers for inter-island maritime legs. Only shipments with vessel details assigned appear here.</p>

        {loading ? (
          <p className="text-sm text-ink-2 py-10">Loading vessel schedule…</p>
        ) : maritime.length === 0 ? (
          <div data-testid="interisland-empty" className="bg-white border-2 border-[#0B132B] rounded-md p-8 text-center shadow-[2px_2px_0px_0px_#0B132B]">
            <Anchor className="h-8 w-8 text-ink-3 mx-auto mb-2" />
            <h2 className="font-heading text-lg font-bold text-ink">No vessel legs assigned yet</h2>
            <p className="text-sm text-ink-2 mt-1">Add a vessel name, sailing time, or dock/terminal slot to a shipment to schedule it here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="interisland-grid">
            {maritime.map((j) => (
              <button
                key={j.id}
                data-testid={`interisland-card-${j.job_number}`}
                onClick={() => navigate(`/app/shipments/${j.id}`)}
                className="text-left bg-white border-2 border-[#0B132B] rounded-md p-4 shadow-[2px_2px_0px_0px_#0B132B] hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] transition-all">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-[10px] font-mono font-bold text-slate-500 uppercase tracking-wider">JOB #{j.job_number}</span>
                  <span className="bg-[#059669] text-white text-[9px] font-black tracking-widest uppercase px-2 py-0.5 rounded">{j.current_stage}</span>
                </div>
                {j.vessel_name && (
                  <div className="mt-3 flex items-center gap-2">
                    <Ship className="h-4 w-4 text-[#0B132B]" />
                    <span className="font-black text-sm text-[#0B132B]">{j.vessel_name}</span>
                  </div>
                )}
                <div className="mt-2.5 space-y-1.5 text-[12px] text-slate-600">
                  {j.departure_sailing_time && (
                    <p className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[#D97706]" /> <strong className="text-[#0B132B]">Sailing:</strong> {fmtSail(j.departure_sailing_time)}</p>
                  )}
                  {j.dock_terminal_slot && (
                    <p className="flex items-center gap-1.5"><Anchor className="h-3.5 w-3.5 text-[#0B132B]" /> <strong className="text-[#0B132B]">Dock/Terminal:</strong> {j.dock_terminal_slot}</p>
                  )}
                  <p className="flex items-center gap-1.5 text-slate-500"><MapPin className="h-3.5 w-3.5" /> {j.pickup_location} → {j.delivery_location}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
